import React, { useEffect, useMemo, useRef, useState } from "react";
import MediaSelectionPopup from "components/MediaSelectionPopup";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { getCustomerHistory, sendMessage, getWhatsappAccount, getEmbeddedSignupByPhone } from "../../../api/login.service";
import { getCookie } from "../../../api/cookie";
import { TabNavigation } from "./components/TabNavigation";
import { AutoMessagesButton } from "./components/AutoMessagesButton";
import { WhatsAppCallsTab } from "./components/WhatsAppCallsTab";
import { PhoneCallsTab } from "./components/PhoneCallsTab";
import { InsightsTab } from "./components/InsightsTab";
import { RecommendationsTab } from "./components/RecommendationsTab";
import { classNames } from "./components/utils";
import { logger } from "../../../utils/logger";

/**
 * WhatsApp-Style Chat UI
 * ---------------------------------------------------
 * Features
 * - Left/right message bubbles (you vs. contact)
 * - Composer with text box + Shift+Enter for newline, Enter to send
 * - Template selector (approved only) with search
 * - Send "approved template" OR plain text
 * - Toggle/Stop auto-messages
 * - Minimal API helpers with graceful fallbacks
 * - Mobile-friendly, accessible
 *
 * Integration
 * <ChatWhatsAppLike
 *   contactId="abc123"
 *   currentUserId="agent-42"
 *   businessId="your-business-id"
 *   apiBaseUrl={process.env.NEXT_PUBLIC_API_BASE}
 *   authToken={yourJWT}
 *   onStopAutoMessages={() => logger.log('auto messages stopped')}
 * />
 */

// ---------- Types ----------
type Sender = "me" | "them";

export type ChatMessage = {
  id: string;
  sender: Sender;
  text: string;
  timestamp: string; // ISO string
  status?: "sending" | "sent" | "failed";
};

export type Template = {
  id: string;
  name: string;
  language?: string;
  category?: string;
  status: "Approved" | "Pending" | "Rejected" | string;
  content: string; // resolved preview text for insertion
  hasImageHeader?: boolean;
  imageUrl?: string;
  components?: Array<{
    type: "HEADER" | "BODY" | "FOOTER" | "BUTTONS";
    format?: "IMAGE" | "TEXT";
    text?: string;
    example?: {
      header_handle?: string[];
      body_text?: string[][];
    };
    buttons?: Array<{
      type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER" | "OTP" | "FLOW";
      text?: string;
      url?: string;
      phone_number?: string;
      otp_type?: string;
      flow_id?: number;
      flow_action?: string;
      navigate_screen?: string;
    }>;
  }>;
};

export type ChatWhatsAppLikeProps = {
  contactId: string; // recipient/user for the thread
  currentUserId: string; // who's sending from this UI (aligns right)
  businessId: string;
  apiBaseUrl?: string; // e.g. https://api.frontdesk24.com/api
  authToken?: string; // bearer token if required by your API
  className?: string;
  onStopAutoMessages?: () => void;
};

// ---------- Languages ----------
const LANGUAGES = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ar_EG', label: 'Arabic (EGY)' },
  { value: 'ar_AE', label: 'Arabic (UAE)' },
  { value: 'ar_LB', label: 'Arabic (LBN)' },
  { value: 'ar_MA', label: 'Arabic (MAR)' },
  { value: 'ar_QA', label: 'Arabic (QAT)' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'be_BY', label: 'Belarusian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bn_IN', label: 'Bengali (IND)' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh_CN', label: 'Chinese (CHN)' },
  { value: 'zh_HK', label: 'Chinese (HKG)' },
  { value: 'zh_TW', label: 'Chinese (TAI)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'prs_AF', label: 'Dari' },
  { value: 'nl', label: 'Dutch' },
  { value: 'nl_BE', label: 'Dutch (BEL)' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_AE', label: 'English (UAE)' },
  { value: 'en_AU', label: 'English (AUS)' },
  { value: 'en_CA', label: 'English (CAN)' },
  { value: 'en_GH', label: 'English (GHA)' },
  { value: 'en_IE', label: 'English (IRL)' },
  { value: 'en_IN', label: 'English (IND)' },
  { value: 'en_JM', label: 'English (JAM)' },
  { value: 'en_MY', label: 'English (MYS)' },
  { value: 'en_NZ', label: 'English (NZL)' },
  { value: 'en_QA', label: 'English (QAT)' },
  { value: 'en_SG', label: 'English (SGP)' },
  { value: 'en_UG', label: 'English (UGA)' },
  { value: 'en_ZA', label: 'English (ZAF)' },
  { value: 'et', label: 'Estonian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'fr_BE', label: 'French (BEL)' },
  { value: 'fr_CA', label: 'French (CAN)' },
  { value: 'fr_CH', label: 'French (CHE)' },
  { value: 'fr_CI', label: 'French (CIV)' },
  { value: 'fr_MA', label: 'French (MAR)' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'de_AT', label: 'German (AUT)' },
  { value: 'de_CH', label: 'German (CHE)' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'rw_RW', label: 'Kinyarwanda' },
  { value: 'ko', label: 'Korean' },
  { value: 'ky_KG', label: 'Kyrgyz (Kyrgyzstan)' },
  { value: 'lo', label: 'Lao' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'nb', label: 'Norwegian' },
  { value: 'ps_AF', label: 'Pashto' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt_BR', label: 'Portuguese (BR)' },
  { value: 'pt_PT', label: 'Portuguese (POR)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'si_LK', label: 'Sinhala' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_AR', label: 'Spanish (ARG)' },
  { value: 'es_CL', label: 'Spanish (CHL)' },
  { value: 'es_CO', label: 'Spanish (COL)' },
  { value: 'es_CR', label: 'Spanish (CRI)' },
  { value: 'es_DO', label: 'Spanish (DOM)' },
  { value: 'es_EC', label: 'Spanish (ECU)' },
  { value: 'es_HN', label: 'Spanish (HND)' },
  { value: 'es_MX', label: 'Spanish (MEX)' },
  { value: 'es_PA', label: 'Spanish (PAN)' },
  { value: 'es_PE', label: 'Spanish (PER)' },
  { value: 'es_ES', label: 'Spanish (SPA)' },
  { value: 'es_UY', label: 'Spanish (URY)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zu', label: 'Zulu' },
];

// Function to detect language from template name
function detectLanguageFromTemplateName(templateName: string): string {
  if (!templateName) return 'en_US';
  
  const nameLower = templateName.toLowerCase();
  
  // Map of language identifiers in template names to language codes
  const languageMap: Record<string, string> = {
    'telugu': 'te',
    'hindi': 'hi',
    'tamil': 'ta',
    'kannada': 'kn',
    'malayalam': 'ml',
    'gujarati': 'gu',
    'marathi': 'mr',
    'punjabi': 'pa',
    'bengali': 'bn',
    'urdu': 'ur',
    'arabic': 'ar',
    'chinese': 'zh_CN',
    'japanese': 'ja',
    'korean': 'ko',
    'spanish': 'es',
    'french': 'fr',
    'german': 'de',
    'italian': 'it',
    'portuguese': 'pt_BR',
    'russian': 'ru',
    'thai': 'th',
    'vietnamese': 'vi',
    'indonesian': 'id',
    'turkish': 'tr',
    'polish': 'pl',
    'dutch': 'nl',
    'swedish': 'sv',
    'norwegian': 'nb',
    'danish': 'da',
    'finnish': 'fi',
    'greek': 'el',
    'hebrew': 'he',
    'persian': 'fa',
    'ukrainian': 'uk',
  };
  
  // Check if template name ends with language identifier (e.g., "_telugu", "-telugu", "telugu")
  for (const [langName, langCode] of Object.entries(languageMap)) {
    if (nameLower.endsWith(`_${langName}`) || nameLower.endsWith(`-${langName}`) || nameLower.endsWith(langName)) {
      return langCode;
    }
  }
  
  // Default to English if no language found
  return 'en_US';
}

// ---------- API helpers (replace with your real endpoints) ----------

type ListResponse = {
  data?: any[];
  items?: any[];
  paging?: { cursors?: { after?: string; before?: string } };
};


// fetch full template by id (body text + metadata)
async function fetchTemplateById(
    apiBaseUrl: string | undefined,
    id: string,
  ): Promise<Template> {
    if (!apiBaseUrl) throw new Error("Missing API base URL");
    const url = new URL(`/api/template/${encodeURIComponent(id)}`, apiBaseUrl);
    const res = await fetch(url.toString(), {
      headers: {
        Accept: "application/json",
        "Authorization": `Bearer ${getCookie('token')}`,
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) throw new Error(`Failed to load template ${id}: ${res.status}`);
    const t = await res.json();
  
    const body = Array.isArray(t?.components)
      ? t.components.find((c: any) => c.type === "BODY")?.text
      : t?.content ?? t?.body ?? "";
    
    // Check if template has image header
    const imageHeader = Array.isArray(t?.components)
      ? t.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")
      : null;
    
    const hasImageHeader = !!imageHeader;
    const imageUrl = imageHeader?.example?.header_handle?.[0] || null;
  
    return {
      id: String(t?.id ?? t?._id ?? id),
      name: String(t?.name ?? t?.templateName ?? ""),
      language: t?.language ?? t?.lang,
      category: t?.category ?? t?.type,
      status: String(t?.status ?? t?.state ?? "Unknown"),
      content: String(body ?? ""),
      hasImageHeader,
      imageUrl,
      components: t?.components || [],
    };
  }
  

  async function fetchMessagesList(
    apiBaseUrl: string | undefined,
    authToken?: string,
    phoneNumberId?: string
  ): Promise<ChatMessage[]> {
    if (!apiBaseUrl) return [];
    const id = phoneNumberId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('phoneNumberId') : null) || '';
    if (!id) return [];
    const url = new URL(`/api/messages/business-number/${encodeURIComponent(id)}`, apiBaseUrl);
    const token = authToken || getCookie('token');
    const res = await fetch(url.toString(), {
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error(`messages HTTP ${res.status}`);

    const json = await res.json();
    const items: any[] = json?.items ?? json?.messages ?? [];
  
    const msgs: ChatMessage[] = [];
    for (const it of items) {
      const ts = String(it.updatedAt ?? new Date().toISOString());
      if (it.lastUserText) {
        msgs.push({
          id: `${it.mid}-u`,
          sender: "them",                 // LEFT
          text: String(it.lastUserText),
          timestamp: ts,
        });
      }
      if (it.lastReplyText) {
        msgs.push({
          id: `${it.mid}-r`,
          sender: "me",                   // RIGHT
          text: String(it.lastReplyText),
          timestamp: ts,
          status: "sent",
        });
      }
    }
  
    // optional: chronological order
    msgs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    return msgs;
  }
  
async function fetchAllApprovedTemplates(
  apiBaseUrl: string | undefined,
): Promise<Template[]> {
  try {
    if (!apiBaseUrl) throw new Error("Missing API base URL");

    // Get WABA ID from session storage
    const wabaId = sessionStorage.getItem('wabaId');
    if (!wabaId) {
      throw new Error("Missing WABA ID");
    }

    const limit = 25;
    let after: string | undefined = undefined;
    const all: any[] = [];

      const url = new URL(`api/list-templates/${wabaId}`, apiBaseUrl);
     //  url.searchParams.set("limit", String(limit));
      // url.searchParams.set("businessId", businessId);
      // if (after) url.searchParams.set("after", after);

      const res = await fetch(url.toString(), {
        headers: {
          Accept: "application/json",
          "Authorization": `Bearer ${getCookie('token')}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error(`Failed to load templates: ${res.status}`);
      const json: ListResponse = await res.json();
      const items: any[] = json?.data ?? json?.items ?? [];
      all.push(...items);

     
      // after = next;
    

    const mapped: Template[] = all
      .map((t: any, i: number) => {
        const body = Array.isArray(t?.components)
          ? t.components.find((c: any) => c.type === "BODY")?.text
          : t?.content ?? t?.body ?? "";
        
        // Check if template has image header
        const imageHeader = Array.isArray(t?.components)
          ? t.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")
          : null;
        
        const hasImageHeader = !!imageHeader;
        const imageUrl = imageHeader?.example?.header_handle?.[0] || null;
        
        return {
          id: String(t?.id ?? t?._id ?? i),
          name: String(t?.name ?? t?.templateName ?? `Template ${i + 1}`),
          language: t?.language ?? t?.lang,
          category: t?.category ?? t?.type,
          status: String(t?.status ?? t?.state ?? "Unknown"),
          content: String(body ?? ""),
          hasImageHeader,
          imageUrl,
          components: t?.components || [],
        } as Template;
      })
      .filter((t) => String(t.status).toLowerCase() === "approved");

    return mapped;
  } catch (err) {
    logger.warn("fetchAllApprovedTemplates fallback:", err);
    return [
      {
        id: "sample-1",
        name: "Appointment Reminder",
        language: "en",
        category: "UTILITY",
        status: "Approved",
        content:
          "Hi {{name}}, this is a reminder for your appointment on {{date}} at {{time}}.",
      },
    ];
  }
}

async function sendPlainTextMessage(
  apiBaseUrl: string | undefined,
  {
    phone,
    message,
    phoneNumberId,
    businessPhoneNumber,
  }: { phone: string; message: string; phoneNumberId: string; businessPhoneNumber: string; }
): Promise<{ id: string }> {
  // Replace with your real send endpoint
  if (!apiBaseUrl) {
    // simulate
    await new Promise((r) => setTimeout(r, 400));
    return { id: `local-${Date.now()}` };
  }
  
  // Add + prefix to customer phone number
  const customerPhoneNumber = phone.startsWith('+') ? phone : `+${phone}`;
  
  const url = new URL("/api/send-message", apiBaseUrl);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify({ 
      customerPhone: customerPhoneNumber, 
      message, 
      businessPhoneNumberId: phoneNumberId,
      businessPhoneNumber,
      preview_url: false 
    }),
  });
  if (!res.ok) throw new Error(`Failed to send message: ${res.status}`);
  const json = await res.json();
  return { id: String(json?.id ?? json?._id ?? Date.now()) };
}

// Generate UUID for flow_token
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// build WhatsApp components payload from body variables in order {{1}}, {{2}}, ...
function buildWAComponentsFromVars(variableKeys: string[], tplVars: Record<string, string>, template: Template) {
    const components: any[] = [];
    
    // Add image header if template has one OR custom header URL is provided
    if ((template.hasImageHeader && template.imageUrl) || tplVars['header_url']) {
      components.push({
        type: "header",
        parameters: [
          {
            type: "image",
            image: {
              link: tplVars['header_url'] || template.imageUrl
            }
          }
        ]
      });
    }
    
    // Add body parameters if there are variables
    if (variableKeys.length > 0) {
      const parameters = variableKeys.map((k) => ({
        type: "text",
        text: String(tplVars[k] ?? ""),
      }));
      components.push({ type: "body", parameters });
    }
    
    // Check if template has buttons and add them to components
    const buttonsComponent = template.components?.find((c: any) => c.type === "BUTTONS");
    logger.log('buildWAComponentsFromVars: Checking for buttons component:', buttonsComponent);
    
    if (buttonsComponent && Array.isArray(buttonsComponent.buttons) && buttonsComponent.buttons.length > 0) {
      logger.log('buildWAComponentsFromVars: Found buttons:', buttonsComponent.buttons);
      
      // Find FLOW buttons specifically - only add if template has FLOW type buttons
      const flowButtons = buttonsComponent.buttons.filter((button: any) => button && button.type === "FLOW");
      logger.log('buildWAComponentsFromVars: Found FLOW buttons:', flowButtons.length);
      
      // Only add flow button component if there are actual FLOW buttons
      if (flowButtons.length > 0) {
        logger.log('buildWAComponentsFromVars: Adding flow button components');
        flowButtons.forEach((button: any, flowIndex: number) => {
          // Find the original index of this FLOW button in the buttons array
          const originalIndex = buttonsComponent.buttons.findIndex((b: any) => b && b.type === "FLOW" && b === button);
          
          // Generate flow_token UUID for this flow button
          const flowToken = generateUUID();
          
          components.push({
            type: "button",
            sub_type: "flow",
            index: String(originalIndex >= 0 ? originalIndex : flowIndex),
            parameters: [
              {
                type: "action",
                action: {
                  flow_token: flowToken
                }
              }
            ]
          });
        });
      } else {
        logger.log('buildWAComponentsFromVars: No FLOW buttons found, skipping flow button component');
      }
      
      // Find dynamic URL buttons (URL buttons with example field AND placeholder in URL)
      // Only add parameters if URL actually contains a placeholder like {{1}}
      const dynamicUrlButtons = buttonsComponent.buttons.filter((button: any) => {
        if (!button || button.type !== "URL") return false;
        // Check if URL contains a placeholder
        const hasPlaceholder = button.url && button.url.match(/\{\{(\d+)\}\}/);
        // Also check for example field (indicates it's dynamic)
        const hasExample = button.example && button.example.length > 0;
        // Only consider it dynamic if it has BOTH placeholder in URL AND example field
        return hasPlaceholder && hasExample;
      });
      
      if (dynamicUrlButtons.length > 0) {
        logger.log('buildWAComponentsFromVars: Found dynamic URL buttons:', dynamicUrlButtons.length);
        dynamicUrlButtons.forEach((button: any) => {
          const buttonIndex = buttonsComponent.buttons.findIndex((b: any) => b === button);
          const buttonKey = `url_button_${buttonIndex}`;
          const urlValue = tplVars[buttonKey];
          
          if (urlValue) {
            // Extract only the dynamic part from the URL
            // WhatsApp API expects only the dynamic part that replaces {{1}}, not the full URL
            let dynamicValue = urlValue;
            
            // Extract the dynamic part from the URL
            if (button.url) {
              const placeholderMatch = button.url.match(/\{\{(\d+)\}\}/);
              if (placeholderMatch) {
                // Extract the base URL (everything before {{1}})
                const baseUrl = button.url.substring(0, button.url.indexOf('{{'));
                // If user entered full URL, extract the dynamic part
                if (urlValue.startsWith(baseUrl)) {
                  dynamicValue = urlValue.substring(baseUrl.length);
                }
                // If user entered just the dynamic part (not starting with http/https), use it as is
              }
            }
            
            components.push({
              type: "button",
              sub_type: "url",
              index: String(buttonIndex),
              parameters: [
                {
                  type: "text",
                  text: dynamicValue
                }
              ]
            });
          }
        });
      }
    } else {
      logger.log('buildWAComponentsFromVars: No BUTTONS component found, or buttons array is empty');
    }
    
    return components;
  }
  
  // normalize language "en" -> "en_US" (adjust as you like)
  function resolveLanguageCode(raw?: string) {
    if (!raw) return "en_US";
    if (raw.includes("_")) return raw;
    return `${raw}_US`;
  }

  
// call your serverless route: /api/send-template-message
  async function sendTemplateMessage(
    apiBaseUrl: string | undefined,
    {
      customerPhone,
      businessPhoneNumberId,
      businessPhoneNumber,
      template_name,
      language_code,
      category,
      components,
    }: {
      customerPhone: string;
      businessPhoneNumberId: string | null;
      businessPhoneNumber: string;
      template_name: string;
      language_code: string;
      category?: string;
      components?: any[];
    }
  ): Promise<{ id: string; message?: string }> {
    if (!apiBaseUrl) {
      await new Promise((r) => setTimeout(r, 500));
      return { id: `local-tpl-${Date.now()}`, message: "Template message sent successfully" };
    }
    const url = new URL("/api/send-template-message", apiBaseUrl);
    
    try {
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Authorization": `Bearer ${getCookie('token')}`,
        },
        body: JSON.stringify({ 
          customerPhone, 
          businessPhoneNumberId, 
          businessPhoneNumber,
          template_name, 
          language_code, 
          category,
          components 
        }),
      });
      
      if (!res.ok) {
        // Try to parse error response
        let errorData;
        try {
          errorData = await res.json();
        } catch {
          // If JSON parsing fails, use status text
          errorData = { message: res.statusText || `HTTP ${res.status}` };
        }
        
        // Create error object with response data
        const error: any = new Error(errorData?.message || `Failed to send template: ${res.status}`);
        error.response = {
          status: res.status,
          data: errorData
        };
        throw error;
      }
      
      const json = await res.json();
      // backend returns { message_id: "...", message: "Template message sent successfully ✔" }
      return { 
        id: String(json?.message_id ?? Date.now()),
        message: json?.message || "Template message sent successfully"
      };
    } catch (fetchError: any) {
      // If it's already an error we created, re-throw it
      if (fetchError.response) {
        throw fetchError;
      }
      
      // Handle network errors or other fetch failures
      logger.error('Network error sending template message:', fetchError);
      const error: any = new Error(fetchError?.message || "Network error. Please check your connection and try again.");
      error.isNetworkError = true;
      throw error;
    }
  }
  
async function stopAutoMessages(
  apiBaseUrl: string | undefined,
  { contactId, phoneNumberId, authToken, botSettings }: { contactId: string; phoneNumberId: string; authToken?: string; botSettings?: boolean }
): Promise<void> {
  try {
    if (!apiBaseUrl) return;
    const url = new URL("/api/messages/bot-settings", apiBaseUrl);
    const res = await fetch(url.toString(), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken || getCookie('token')}`,
      },
      body: JSON.stringify({
        customerNumber: contactId,
        phone_number_id: phoneNumberId,
        bot_settings: botSettings ?? false,
      }),
    });
    if (!res.ok) throw new Error(`Failed to update auto messages: ${res.status}`);
  } catch (e) {
    logger.warn(e);
    throw e;
  }
}

// ---------- Helpers ----------
const fmtTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

// classNames utility moved to ./components/utils.ts

// ---------- UI Subcomponents ----------
function Header({
  contactName,
  contactPhone,
  onBack,
}: {
  contactName: string;
  contactPhone?: string;
  onBack?: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b px-4 py-3">
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="mr-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
          >
            ← Back
          </button>
        )}
        <div className="h-9 w-9 rounded-full bg-gray-200" />
        <div>
          <div className="font-semibold leading-tight">{contactName}</div>
          {contactPhone && (
            <div className="text-xs text-gray-500">{contactPhone}</div>
          )}
          <div className="text-xs text-gray-500">WhatsApp • Direct Messages</div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isMe = msg.sender === "me";
  return (
    <div className={classNames("flex w-full", isMe ? "justify-end" : "justify-start")}>
      <div
        className={classNames(
          "relative max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow",
          isMe ? "bg-green-500 text-white" : "bg-white border"
        )}
      >
        <div className="whitespace-pre-wrap break-words">{msg.text}</div>
        <div
          className={classNames(
            "mt-1 text-[10px] opacity-80",
            isMe ? "text-white" : "text-gray-500"
          )}
        >
          {fmtTime(msg.timestamp)}
          {isMe && msg.status && (
            <span className="ml-1">{msg.status === "sent" ? "✓✓" : msg.status === "sending" ? "…" : "⚠"}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Conversation History Display Component
function ConversationHistoryDisplay({ conversations }: { conversations: any[] }) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  
  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [conversations.length]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    // Use toLocaleString with user's locale and timezone
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div 
      ref={scrollerRef}
      className="h-96 overflow-y-auto bg-gray-50 p-4 space-y-4"
    >
      {conversations.map((conversation, index) => (
        <div key={conversation.messageId || index} className="space-y-2">
          {/* Left messages (customer) */}
          {conversation.left.map((message: any, msgIndex: number) => (
            <div key={`left-${msgIndex}`} className="flex justify-start">
              <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-white shadow-sm border">
                <div className="text-sm text-gray-800">{message.message}</div>
                <div className="text-xs text-gray-500 mt-1">{formatTimestamp(message.timestamp)}</div>
              </div>
            </div>
          ))}
          
          {/* Right messages (bot) */}
          {conversation.right.map((message: any, msgIndex: number) => (
            <div key={`right-${msgIndex}`} className="flex justify-end">
              <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-blue-500 text-white shadow-sm">
                <div className="text-sm">{message.message}</div>
                <div className="text-xs text-blue-100 mt-1">{formatTimestamp(message.timestamp)}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MessageList({ messages }: { messages: ChatMessage[] }) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [showJump, setShowJump] = useState(false);
  
    const scrollToBottom = () => {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    };
  
    useEffect(() => {
      scrollToBottom(); // auto-stick to bottom on new messages
    }, [messages.length]);
  
    const onScroll = () => {
      const el = scrollerRef.current;
      if (!el) return;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      setShowJump(!atBottom);
    };
  
    return (
      <div className="relative">
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\'%3E%3Crect width=\'12\' height=\'12\' fill=\'%23f6f6f6\'/%3E%3C/svg%3E")',
            // prevent layout jump on new content
            overflowAnchor: "none",
          }}
          className="max-h-[60vh] min-h-[260px] overscroll-contain overflow-y-auto p-4"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-2">
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
          </div>
        </div>
  
        {showJump && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-3 right-4 rounded-full bg-white/90 px-3 py-1 text-xs shadow hover:bg-white"
            aria-label="Jump to latest message"
          >
            ↓ Jump to latest
          </button>
        )}
      </div>
    );
  }
  
function TemplateSelector({
  templates,
  selectedId,
  onSelect,
}: {
  templates: Template[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="w-full">
      <label className="mb-1 block text-xs font-medium">Approved templates</label>
      <select
        className="w-full rounded-lg border px-2 py-2 text-sm"
        value={selectedId ?? ""}
        onChange={(e) => onSelect(e.target.value || null)}
      >
        <option value="">— Select a template —</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} - {t.category ? ` ${t.category}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function Composer({
  mode,
  setMode,
  draft,
  setDraft,
  onSendPlain,
  onSendTemplate,
  sending,
  canSend,
  templates,
  selectedTemplateId,
  onSelectTemplate,
  selectedTemplate,
  tplVars,
  setTplVars,
  variableKeys,
  messages,
  conversationHistory,
  selectedLanguage,
  setSelectedLanguage,
}: {
  mode: "plain" | "template";
  setMode: (m: "plain" | "template") => void;
  draft: string;
  setDraft: (s: string) => void;
  onSendPlain: () => void;
  onSendTemplate: () => void;
  sending: boolean;
  canSend: boolean;
  templates: Template[];
  selectedTemplateId: string | null;
  onSelectTemplate: (id: string | null) => void;
  selectedTemplate: Template | null;
  tplVars: Record<string, string>;
  setTplVars: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  variableKeys: string[];
  messages: ChatMessage[];
  conversationHistory: any;
  selectedLanguage: string;
  setSelectedLanguage: (lang: string) => void;
}) {
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  
  // Auto-detect and set language when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const detectedLang = detectLanguageFromTemplateName(selectedTemplate.name);
      setSelectedLanguage(detectedLang);
    }
  }, [selectedTemplate, setSelectedLanguage]);
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (mode === "plain") onSendPlain();
      else onSendTemplate();
    }
  };

  // In template mode, only enable send when a template is selected AND all variables are filled
  // Check for dynamic URL buttons (must have example field AND placeholder in URL)
  const buttonsComponent = selectedTemplate?.components?.find((c: any) => c.type === "BUTTONS");
  const dynamicUrlButtons = buttonsComponent?.buttons?.filter((btn: any) => 
    btn.type === "URL" && btn.example && btn.example.length > 0 && btn.url && btn.url.match(/\{\{(\d+)\}\}/)
  ) || [];
  
  const allVarsFilled =
    mode === "template" &&
    !!selectedTemplate &&
    variableKeys.every((k) => (tplVars[k] ?? "").trim().length > 0) &&
    dynamicUrlButtons.every((btn: any) => {
      const buttonIndex = buttonsComponent.buttons.findIndex((b: any) => b === btn);
      const buttonKey = `url_button_${buttonIndex}`;
      return (tplVars[buttonKey] ?? "").trim().length > 0;
    });

  // Check if conversation is within 24-hour window - only check USER messages (sender: "them")
  const isWithin24Hours = () => {
    // If we have conversationHistory, use that
    if (conversationHistory && conversationHistory.conversations && conversationHistory.conversations.length > 0) {
      // Get the last conversation's messages
      const lastConv = conversationHistory.conversations[conversationHistory.conversations.length - 1];
      const customerMessages = lastConv.left || [];
      if (customerMessages.length === 0) return true;
      
      const lastCustomerMsg = customerMessages[customerMessages.length - 1];
      const lastMessageTime = new Date(lastCustomerMsg.timestamp);
      const now = new Date();
      const hoursSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
      
      return hoursSinceLastMessage <= 24;
    }
    
    // Fallback to messages array
    if (messages.length === 0) return true; // No messages yet
    
    // Find the last user message (sender: "them")
    const userMessages = messages.filter(msg => msg.sender === "them");
    if (userMessages.length === 0) return true; // No user messages yet
    
    const lastUserMessage = userMessages[userMessages.length - 1];
    const lastMessageTime = new Date(lastUserMessage.timestamp);
    const now = new Date();
    const hoursSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
    
    return hoursSinceLastMessage <= 24;
  };

  const within24Hours = isWithin24Hours();
  const displayWarning = mode === "plain" && !within24Hours;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sendmode"
            checked={mode === "plain"}
            onChange={() => setMode("plain")}
          />
          Plain text
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sendmode"
            checked={mode === "template"}
            onChange={() => setMode("template")}
          />
          Approved template
        </label>
      </div>

      {mode === "plain" ? (
        <div className="flex flex-col gap-2">
          {displayWarning && (
            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              ⚠️ This conversation is outside the 24-hour messaging window. Please switch to "Approved template" to send a message.
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              placeholder={
                "Type a message (Enter to send, Shift+Enter for newline)"
              }
              className={classNames(
                "min-h-[44px] flex-1 resize-y rounded-2xl border px-3 py-2 shadow-sm focus:outline-none",
                displayWarning ? "border-orange-300 bg-orange-50" : ""
              )}
              disabled={displayWarning}
            />
            <div className="relative group">
              <button
                onClick={onSendPlain}
                disabled={!canSend || sending || displayWarning}
                title={displayWarning ? "Outside 24-hour window. Use approved template." : ""}
                className={classNames(
                  "rounded-2xl px-4 py-2 text-sm font-medium shadow-sm transition",
                  !canSend || sending || displayWarning
                    ? "cursor-not-allowed bg-gray-200 text-gray-500"
                    : "bg-green-500 text-white hover:bg-green-600"
                )}
                aria-label="Send message"
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border bg-gray-50 p-3">
          {/* Template selector and language dropdown */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <TemplateSelector
                templates={templates}
                selectedId={selectedTemplateId}
                onSelect={(id) => onSelectTemplate(id)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Language *</label>
              <select
                className="w-full rounded-lg border px-2 py-2 text-sm"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end justify-end">
              <button
                onClick={onSendTemplate}
                disabled={!allVarsFilled || sending}
                className={classNames(
                  "rounded-2xl px-4 py-2 text-sm font-medium shadow-sm transition",
                  !allVarsFilled || sending
                    ? "cursor-not-allowed bg-gray-200 text-gray-500"
                    : "bg-green-500 text-white hover:bg-green-600"
                )}
                aria-label="Send template"
              >
                {sending ? "Sending…" : "Send template"}
              </button>
            </div>
          </div>

          {/* Only show variables AFTER a template is selected */}
          {selectedTemplate ? (
            (() => {
              // Check for body variables
              const hasBodyVariables = variableKeys.length > 0;
              // Check for image header
              const hasImageHeader = selectedTemplate?.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE");
              // Check for dynamic URL buttons (buttons with example field)
              const buttonsComponent = selectedTemplate?.components?.find((c: any) => c.type === "BUTTONS");
              const hasDynamicUrlButtons = buttonsComponent?.buttons?.some((btn: any) => 
                btn.type === "URL" && btn.example && btn.example.length > 0 && btn.url && btn.url.match(/\{\{(\d+)\}\}/)
              ) || false;
              
              return hasBodyVariables || hasImageHeader || hasDynamicUrlButtons;
            })() ? (
              <div className="mt-3">
                {variableKeys.length > 0 && (
                  <>
                    <div className="mb-2 text-xs font-semibold text-gray-600">Template variables</div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      {variableKeys.map((k) => (
                        <label key={k} className="text-xs">
                          <span className="mb-1 block text-gray-600">{k}</span>
                          <input
                            className="w-full rounded-md border px-2 py-1"
                            value={tplVars[k] ?? ""}
                            onChange={(e) => setTplVars((prev) => ({ ...prev, [k]: e.target.value }))}
                            placeholder={`Enter ${k}`}
                          />
                        </label>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Header Image selection - only if template requires IMAGE header */}
                {selectedTemplate?.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE") && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Header Image
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowMediaPicker(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                      >
                        <span>Select from Media Files</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Dynamic URL Buttons - show input fields if URL buttons have examples */}
                {(() => {
                  const buttonsComponent = selectedTemplate?.components?.find((c: any) => c.type === "BUTTONS");
                  const dynamicUrlButtons = buttonsComponent?.buttons?.filter((btn: any) => 
                    btn.type === "URL" && btn.example && btn.example.length > 0
                  ) || [];
                  
                  if (dynamicUrlButtons.length > 0) {
                    return (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-2">
                          URL Button Values
                        </label>
                        <div className="space-y-2">
                          {dynamicUrlButtons.map((btn: any, btnIndex: number) => {
                            const buttonIndex = buttonsComponent.buttons.findIndex((b: any) => b === btn);
                            const buttonKey = `url_button_${buttonIndex}`;
                            // Extract the dynamic part from example URL to show as placeholder
                            const templateUrl = btn.url || '';
                            const placeholderMatch = templateUrl.match(/\{\{(\d+)\}\}/);
                            let placeholderText = btn.example?.[0] || "Enter dynamic URL value";
                            
                            if (placeholderMatch && btn.example?.[0]) {
                              // Extract just the dynamic part from example
                              const baseUrl = templateUrl.substring(0, templateUrl.indexOf('{{'));
                              if (btn.example[0].startsWith(baseUrl)) {
                                placeholderText = btn.example[0].substring(baseUrl.length);
                              }
                            }
                            
                            return (
                              <div key={buttonIndex}>
                                <label className="block text-xs text-gray-600 mb-1">
                                  {btn.text} - Dynamic Value
                                  <span className="text-gray-400 ml-1">(Template: {templateUrl})</span>
                                </label>
                                <input
                                  className="w-full rounded-md border px-2 py-1 text-xs"
                                  value={tplVars[buttonKey] ?? ""}
                                  onChange={(e) => setTplVars((prev) => ({ ...prev, [buttonKey]: e.target.value }))}
                                  placeholder={placeholderText}
                                />
                                <div className="text-[10px] text-gray-500 mt-1">
                                  Enter only the dynamic part that replaces {templateUrl.match(/\{\{(\d+)\}\}/)?.[0] || '{{1}}'} in the URL
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="mt-3 rounded-lg border bg-white p-3">
                    <div className="mb-2 font-medium text-sm">Preview</div>
                    <div className="bg-gray-50 rounded-lg p-3 max-w-sm mx-auto">
                      {/* WhatsApp-style message bubble */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        {/* Header Image - only show if template has IMAGE header */}
                        {selectedTemplate?.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE") && (
                          <div className="w-full">
                            <img 
                              src={tplVars['header_url'] || selectedTemplate.imageUrl || selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0]}
                              alt="Template header"
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                logger.error('Header image failed to load:', e.currentTarget.src);
                                e.currentTarget.style.display = 'none';
                              }}
                              onLoad={() => {
                                logger.log('Header image loaded successfully:', tplVars['header_url'] || selectedTemplate.imageUrl);
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Body Text */}
                        <div className="p-3">
                          <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                            {selectedTemplate.components?.find(c => c.type === "BODY")?.text?.replace(
                              /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g,
                              (_m, p1) => tplVars[p1] ?? `{{${p1}}}`
                            ) || selectedTemplate.content.replace(
                              /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g,
                              (_m, p1) => tplVars[p1] ?? `{{${p1}}}`
                            )}
                          </div>
                        </div>
                        
                        {/* Footer */}
                        {(() => {
                          const footerComponent = selectedTemplate.components?.find(c => c.type === "FOOTER");
                          logger.log('Footer debug - components:', selectedTemplate.components);
                          logger.log('Footer debug - footerComponent:', footerComponent);
                          return footerComponent ? (
                            <div className="px-3 pb-3">
                              <div className="text-xs text-gray-500 border-t pt-2">
                                {footerComponent.text}
                              </div>
                            </div>
                          ) : (
                            <div className="px-3 pb-3">
                              <div className="text-xs text-red-500 border-t pt-2">
                                Footer not found - Components: {selectedTemplate.components?.length || 0}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                </div>
                {!allVarsFilled && (
                  <div className="mt-2 text-[11px] text-gray-500">Fill all variables to enable sending.</div>
                )}
              </div>
            ) : (
              <div className="mt-3 text-xs text-gray-600">This template has no variables.</div>
            )
          ) : (
            <div className="mt-3 text-xs text-gray-500">Select an approved template to continue.</div>
          )}
      {/* Media Picker Popup */}
      {showMediaPicker && (
        <MediaSelectionPopup
          isOpen={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(url: string) => {
            setTplVars(prev => ({ ...prev, 'header_url': url }));
            setShowMediaPicker(false);
          }}
          title="Select Header Image"
        />
      )}
        </div>
      )}
    </div>
  );
}

// Tab components extracted to separate files in ./components/

// ---------- Main Component ----------
export default function ChatWhatsAppLike({
  contactId,
  currentUserId,
  businessId,
  apiBaseUrl,
  authToken,
  className,
  onStopAutoMessages,
}: ChatWhatsAppLikeProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { phoneNumber } = useParams<{ phoneNumber?: string }>();
  const location = useLocation();
  
  // Get URL query parameters for testing
  const participantIdentity = searchParams.get("participantIdentity") || null;
  const participantName = searchParams.get("participantName") || null;
  
  // Determine active tab from URL path
  const getTabFromPath = (path: string): string => {
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/whatsappcalls')) return 'whatsapp-calls';
    if (path.includes('/phonecalls')) return 'phone-calls';
    if (path.includes('/insights')) return 'insights';
    if (path.includes('/recommendations')) return 'recommendations';
    
    // Fallback: check if there's an incoming call
    const incomingCallStatus = sessionStorage.getItem('incomingCallStatus');
    if (incomingCallStatus && incomingCallStatus !== 'answered' && incomingCallStatus !== 'ended') {
      return 'whatsapp-calls';
    }
    
    return 'messages'; // Default to messages tab
  };
  
  const [activeTab, setActiveTab] = useState<string>(getTabFromPath(location.pathname));
  
  // Sync activeTab with URL when route changes
  useEffect(() => {
    const tabFromPath = getTabFromPath(location.pathname);
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname, activeTab]);

  // Log URL parameters for testing
  useEffect(() => {
    if (participantIdentity || participantName) {
      logger.log("🔗 URL Parameters detected:", {
        participantIdentity,
        participantName,
        currentTab: activeTab,
      });
    }
  }, [participantIdentity, participantName, activeTab]);
  
  // Get customer data from session storage
  const [customerData, setCustomerData] = useState<{phone: string, name: string} | null>(null);
  const [conversationHistory, setConversationHistory] = useState<any>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState<string>('');
  
  useEffect(() => {
    // Priority: URL param > sessionStorage > contactId prop
    let customerPhone = phoneNumber || contactId;
    const storedCustomer = sessionStorage.getItem('selectedCustomer');
    
    const initCustomerAndHistory = async (phone: string) => {
      const id = await getPhoneNumberId();
      if (id) {
        await fetchConversationHistory(phone, id);
      } else {
        logger.warn('No phoneNumberId — conversation history and send may not work until login/phone is set');
      }
    };

    if (storedCustomer) {
      try {
        const customer = JSON.parse(storedCustomer);
        // Use URL param if available, otherwise use sessionStorage
        if (phoneNumber) {
          customerPhone = phoneNumber;
          // Update sessionStorage with URL param
          sessionStorage.setItem('selectedCustomer', JSON.stringify({
            phone: phoneNumber,
            name: customer.name || phoneNumber
          }));
        } else {
          customerPhone = customer.phone || customerPhone;
        }
        setCustomerData({
          phone: customerPhone,
          name: customer.name || customerPhone
        });
        if (customerPhone) {
          initCustomerAndHistory(customerPhone).catch(err => {
            logger.error('Error initializing chat:', err);
          });
        }
      } catch (error) {
        logger.error('Error parsing customer data:', error);
      }
    } else if (customerPhone) {
      // If no sessionStorage but we have phone from URL or prop
      setCustomerData({
        phone: customerPhone,
        name: customerPhone
      });
      initCustomerAndHistory(customerPhone).catch(err => {
        logger.error('Error initializing chat:', err);
      });
    }
    
    // Check if there's an incoming call and auto-connect
    const incomingCallRoomName = sessionStorage.getItem('incomingCallRoomName');
    const incomingCallStatus = sessionStorage.getItem('incomingCallStatus');
    const incomingCallCalleeId = sessionStorage.getItem('incomingCallCalleeId');
    const incomingCallCallerId = sessionStorage.getItem('incomingCallCallerId');
    
    if (incomingCallStatus && incomingCallStatus !== 'answered' && incomingCallStatus !== 'ended') {
      logger.log('=== INCOMING CALL DETECTED ON PAGE ===');
      logger.log('Room name:', incomingCallRoomName);
      logger.log('Call status:', incomingCallStatus);
      logger.log('Callee ID:', incomingCallCalleeId);
      logger.log('Caller ID:', incomingCallCallerId);
      
      // Switch to WhatsApp Calls tab if not already on a specific tab route
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/messages') && 
          !currentPath.includes('/whatsappcalls') && 
          !currentPath.includes('/phonecalls') && 
          !currentPath.includes('/insights') && 
          !currentPath.includes('/recommendations')) {
        // Only navigate if we're on the base route without a tab
        navigate(`/events/whatsapp-send-message/${customerPhone}/whatsappcalls`);
      }
    }
  }, [phoneNumber, contactId, navigate]);

  const getPhoneNumberId = async (): Promise<string | null> => {
    logger.log('=== GETTING PHONE NUMBER ID ===');
    
    // First, try to get phoneNumberId from session storage (stored during login)
    let phoneNumberId = sessionStorage.getItem('phoneNumberId');
    logger.log('PhoneNumberId from session storage:', phoneNumberId);
    
    if (phoneNumberId) {
      setPhoneNumberId(phoneNumberId);
      logger.log('PhoneNumberId set from session storage:', phoneNumberId);
      return phoneNumberId;
    }
    
    // If not in sessionStorage, try multiple methods to fetch it
    try {
      const wabaId = sessionStorage.getItem('wabaId');
      const email = localStorage.getItem('email') || sessionStorage.getItem('email');
      const displayPhoneNumber = sessionStorage.getItem('display_phone_number');
      
      logger.log('Attempting to fetch phoneNumberId:', { wabaId, email, displayPhoneNumber });
      
      // Method 1: Try fetching from WhatsApp accounts API using email
      if (email) {
        try {
          logger.log('Method 1: Fetching from getWhatsappAccount API with email...');
          const accounts = await getWhatsappAccount(email);
          logger.log('Accounts received:', accounts);
          
          if (Array.isArray(accounts) && accounts.length > 0) {
            // If we have wabaId, try to match it
            if (wabaId) {
              const account = accounts.find((acc: any) => acc.wabaId === wabaId);
              if (account && account.phoneNumberId) {
                phoneNumberId = account.phoneNumberId;
                logger.log('Found phoneNumberId from API (matched wabaId):', phoneNumberId);
              }
            }
            
            // If still not found, use the first account's phoneNumberId
            if (!phoneNumberId && accounts[0]?.phoneNumberId) {
              phoneNumberId = accounts[0].phoneNumberId;
              logger.log('Using first account phoneNumberId:', phoneNumberId);
            }
          }
        } catch (apiError) {
          logger.warn('Method 1 failed:', apiError);
        }
      }
      
      // Method 2: Try fetching from embedded signup using phone number
      if (!phoneNumberId && displayPhoneNumber) {
        try {
          logger.log('Method 2: Fetching from getEmbeddedSignupByPhone API...');
          const embeddedData = await getEmbeddedSignupByPhone(displayPhoneNumber);
          
          if (embeddedData?.records && embeddedData.records.length > 0) {
            const record = embeddedData.records[0];
            if (record.phoneNumberId) {
              phoneNumberId = record.phoneNumberId;
              logger.log('Found phoneNumberId from embedded signup:', phoneNumberId);
            }
          }
        } catch (embeddedError) {
          logger.warn('Method 2 failed:', embeddedError);
        }
      }
      
      // If we found it, store it and set it
      if (phoneNumberId) {
        sessionStorage.setItem('phoneNumberId', phoneNumberId);
        setPhoneNumberId(phoneNumberId);
        logger.log('PhoneNumberId successfully retrieved and stored:', phoneNumberId);
        // Clear any previous error
        setError(null);
        return phoneNumberId;
      }
      
      // If still not found after all methods, show error
      logger.error('No phoneNumberId found after all attempts');
      logger.error('Available data:', {
        wabaId,
        email,
        displayPhoneNumber,
        hasSessionStorage: !!sessionStorage.getItem('phoneNumberId')
      });
      
      // Only show error if we're actually trying to send a message
      // Don't show error on initial load - it might be available later
      setError('Phone number ID not available. Some features may be limited.');
    } catch (error) {
      logger.error('Error fetching phoneNumberId:', error);
      // Don't show blocking error - just log it
      setError('Unable to retrieve phone number ID. Some features may be limited.');
    }
    return null;
  };

  const fetchConversationHistory = async (customerNumber: string, phoneNumberId: string) => {
    setConversationLoading(true);
    try {
      const result = await getCustomerHistory(customerNumber, phoneNumberId);
      setConversationHistory(result);
      // Update autoEnabled based on bot_settings from API
      if (result && typeof (result as any).bot_settings === 'boolean') {
        setAutoEnabled((result as any).bot_settings);
      }
    } catch (error) {
      logger.error('Error fetching conversation history:', error);
    } finally {
      setConversationLoading(false);
    }
  };
  
  const handleBackNavigation = () => {
    // Simply navigate back to the phone numbers list
    navigate('/events/conversations');
  };

  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [mode, setMode] = useState<"plain" | "template">("plain");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en_US');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const list = await fetchAllApprovedTemplates(apiBaseUrl);
      if (!mounted) return;
      setTemplates(list);
      // auto-select the first approved template for convenience
      const firstApproved = list.find((t) => String(t.status).toLowerCase() === "approved");
      if (firstApproved) setSelectedTemplateId(firstApproved.id);
    })();
    return () => {
      mounted = false;
    };
  }, [apiBaseUrl]);


  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  // Debug logging for selected template
  useEffect(() => {
    if (selectedTemplate) {
      logger.log('Selected template:', selectedTemplate);
      const headerComponent = selectedTemplate.components?.find(c => c.type === "HEADER" && c.format === "IMAGE");
      logger.log('Header component:', headerComponent);
      if (headerComponent) {
        logger.log('Image URL:', headerComponent.example?.header_handle?.[0]);
      }
      const footerComponent = selectedTemplate.components?.find(c => c.type === "FOOTER");
      logger.log('Footer component:', footerComponent);
    }
  }, [selectedTemplate]);

  const variableKeys = useMemo(() => {
    if (!selectedTemplate) return [] as string[];
    return parseTemplateVariables(selectedTemplate.content);
  }, [selectedTemplate?.id, selectedTemplate?.content]);

  const canSend = useMemo(() => {
    if (sending) return false;
    if (mode === "plain") return draft.trim().length > 0;
    // for template mode, allow sending even with empty draft — backend will use template + variables
    return Boolean(selectedTemplate);
  }, [draft, mode, selectedTemplate, sending]);

  async function handleSendPlain() {
    logger.log('=== SEND BUTTON CLICKED ===');
    logger.log('customerData:', customerData);
    logger.log('phoneNumberId:', phoneNumberId);
    logger.log('draft:', draft);
    
    const text = draft.trim();
    if (!text) {
      logger.log('No text to send');
      return;
    }
    if (!customerData?.phone) {
      logger.log('Customer phone not available');
      setError('Customer phone number not available');
      return;
    }
    let resolvedPhoneNumberId =
      phoneNumberId || sessionStorage.getItem('phoneNumberId') || '';
    if (!resolvedPhoneNumberId) {
      logger.log('PhoneNumberId not available, attempting to fetch...');
      resolvedPhoneNumberId = (await getPhoneNumberId()) || '';
    }
    if (!resolvedPhoneNumberId) {
      setError('Business phone number ID not available. Please refresh the page or log in again.');
      return;
    }
    if (resolvedPhoneNumberId !== phoneNumberId) {
      setPhoneNumberId(resolvedPhoneNumberId);
    }
    
    // Check 24-hour window for plain text messages (prefer conversation history)
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    if (lastMessage) {
      const lastMessageTime = new Date(lastMessage.timestamp);
      const now = new Date();
      const hoursSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
      
      logger.log('Time since last message:', hoursSinceLastMessage, 'hours');
      
      if (hoursSinceLastMessage > 24) {
        logger.log('Outside 24-hour window, requiring template');
        setError('This conversation is outside the 24-hour messaging window. Please use an approved template to send a message.');
        // Switch to template mode and show the error
        return;
      }
    }
    
    logger.log('About to send message:', { phone: customerData.phone, message: text, phoneNumberId: resolvedPhoneNumberId });
    
    setError(null);
    setSending(true);

    const tempId = `temp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      sender: "me",
      text,
      timestamp: new Date().toISOString(),
      status: "sending",
    };
    setMessages((m) => [...m, optimistic]);

    try {
      logger.log('Calling sendMessage API...');
      // Get business phone number from session storage (stored during login)
      const businessPhoneNumber = sessionStorage.getItem('display_phone_number')?.replace(/[^\d]/g, '') || '';
      logger.log('Business phone number:', businessPhoneNumber);
      
      const result = await sendMessage(
        customerData.phone,
        text,
        resolvedPhoneNumberId,
        businessPhoneNumber
      );
      logger.log('API response received:', result);
      setMessages((m) =>
        m.map((mm) => (mm.id === tempId ? { ...mm, id: result.messageId || `msg-${Date.now()}`, status: "sent" } : mm))
      );
      setDraft("");
      
      // Refresh conversation history after sending message
      if (customerData?.phone) {
        const phoneNumberId = sessionStorage.getItem('phoneNumberId') || '';
        if (phoneNumberId) await fetchConversationHistory(customerData.phone, phoneNumberId);
      }
    } catch (e: any) {
      logger.error('Send message error:', e);
      setMessages((m) =>
        m.map((mm) => (mm.id === tempId ? { ...mm, status: "failed" } : mm))
      );
      setError(e?.message ?? "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  // Simple variable parser for {{var}} tokens in the template content
  function parseTemplateVariables(content: string) {
    const vars = new Set<string>();
    const re = /\{\{\s*([a-zA-Z0-9_\.\-]+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(content))) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  }

  const [tplVars, setTplVars] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!selectedTemplateId) return;
    (async () => {
      try {
        const full = await fetchTemplateById(apiBaseUrl, selectedTemplateId);
        // merge the fetched details into the list so selectedTemplate updates
        setTemplates(prev =>
          prev.map(t => (t.id === selectedTemplateId ? { ...t, ...full } : t))
        );
      } catch (e) {
        // optional: setError((e as any)?.message ?? "Failed to load template details");
      }
    })();
  }, [selectedTemplateId, apiBaseUrl]);
  

  async function handleSendTemplate() {
    if (!selectedTemplate) return;
    setError(null);
    setSuccess(null); // Clear previous success message
    setSending(true);

    const filled = Object.keys(tplVars).reduce((acc, k) => acc && tplVars[k].trim().length > 0, true);
    if (!filled && selectedTemplate.content.includes("{{")) {
      setSending(false);
      setError("Please fill in all template variables before sending.");
      return;
    }

    const preview = selectedTemplate.content.replace(/\{\{\s*([a-zA-Z0-9_\.\-]+)\s*\}\}/g, (_m, p1) => tplVars[p1] ?? "");

    const tempId = `temp-tpl-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      sender: "me",
      text: preview || `[Template: ${selectedTemplate.name}]`,
      timestamp: new Date().toISOString(),
      status: "sending",
    };
    setMessages((m) => [...m, optimistic]);

    try {
        const language_code = selectedLanguage || resolveLanguageCode(selectedTemplate.language);
        const components = buildWAComponentsFromVars(variableKeys, tplVars, selectedTemplate);
        
        // Get business phone number ID from session storage
        const businessPhoneNumberId = sessionStorage.getItem('phoneNumberId');
        
        // Get business phone number (login user's number) from session storage
        const businessPhoneNumber = sessionStorage.getItem('display_phone_number') || sessionStorage.getItem('businessPhoneNumber');
        
        if (!businessPhoneNumberId) {
          setSending(false);
          setError('Business phone number ID is missing. Please log in again.');
          return;
        }
        
        if (!businessPhoneNumber) {
          setSending(false);
          setError('Business phone number is missing. Please log in again.');
          return;
        }
        
        // Use customer phone from customerData (from session storage) if available, otherwise use contactId
        let customerPhoneNumber: string;
        if (customerData && customerData.phone) {
          // Use phone from customerData (stored in session storage)
          customerPhoneNumber = customerData.phone.replace(/\D/g, ''); // Remove all non-digits
          logger.log('Using customer phone from customerData:', customerPhoneNumber);
        } else {
          // Fallback to contactId if customerData is not available
          customerPhoneNumber = contactId.replace(/\D/g, ''); // Remove all non-digits
          logger.log('Using contactId as fallback:', customerPhoneNumber);
        }
        
        // Format phone number with + prefix if not present
        const formattedCustomerPhone = customerPhoneNumber.startsWith('+') ? customerPhoneNumber : `+${customerPhoneNumber}`;
        
        logger.log('=== SENDING TEMPLATE MESSAGE ===');
        logger.log('Template:', selectedTemplate.name);
        logger.log('Category:', selectedTemplate.category);
        logger.log('Language:', language_code);
        logger.log('Customer Phone:', formattedCustomerPhone);
        logger.log('Business Phone Number ID:', businessPhoneNumberId);
        logger.log('Business Phone Number:', businessPhoneNumber);
        logger.log('Variables:', tplVars);
        logger.log('Header URL:', tplVars['header_url']);
        logger.log('Components payload:', components);
        logger.log('Full payload will be sent to:', `${apiBaseUrl}/api/send-template-message`);
        
        if (!formattedCustomerPhone || formattedCustomerPhone === '+') {
          setSending(false);
          setError('Customer phone number is missing. Cannot send message.');
          return;
        }
        
        try {
          const { id, message } = await sendTemplateMessage(apiBaseUrl, {
            customerPhone: formattedCustomerPhone,
            businessPhoneNumberId: businessPhoneNumberId,
            businessPhoneNumber: businessPhoneNumber,
            template_name: selectedTemplate.name,
            language_code,
            category: selectedTemplate.category,
            components,
          });
          
          logger.log('Template message sent successfully, message ID:', id);
          logger.log('Success message from API:', message);
          
          setMessages((m) =>
            m.map((mm) => (mm.id === tempId ? { ...mm, id, status: "sent" } : mm))
          );
          setDraft("");
          setError(null); // Clear any previous errors
          setSuccess(message || "Template message sent successfully ✔"); // Show success message from API
          
          // Auto-hide success message after 5 seconds
          setTimeout(() => {
            setSuccess(null);
          }, 5000);
        } catch (apiError: any) {
          logger.error('=== ERROR SENDING TEMPLATE MESSAGE ===');
          logger.error('Error details:', apiError);
          logger.error('Error message:', apiError?.message);
          logger.error('Error response:', apiError?.response);
          logger.error('Error status:', apiError?.response?.status);
          logger.error('Error data:', apiError?.response?.data);
          
          // Extract detailed error message
          let errorMessage = "Failed to send template message.";
          
          if (apiError?.response) {
            const status = apiError.response.status;
            const data = apiError.response.data;
            
            if (status === 401 || status === 402 || status === 403) {
              errorMessage = "Authentication failed. Please refresh the page and try again.";
            } else if (status === 400) {
              errorMessage = data?.message || data?.error || "Invalid request. Please check template variables and try again.";
            } else if (status === 404) {
              errorMessage = "Template not found. Please select a valid template.";
            } else if (status === 429) {
              errorMessage = "Rate limit exceeded. Please wait a moment and try again.";
            } else if (status >= 500) {
              errorMessage = "Server error. Please try again later.";
            } else {
              errorMessage = data?.message || data?.error || `Failed to send message (${status}).`;
            }
          } else if (apiError?.message) {
            errorMessage = apiError.message;
          }
          
          setMessages((m) =>
            m.map((mm) => (mm.id === tempId ? { ...mm, status: "failed" } : mm))
          );
          setError(errorMessage);
          setSuccess(null); // Clear success message on error
          throw apiError; // Re-throw to be caught by outer catch
        }
    } catch (e: any) {
      // This catch block handles any errors that weren't caught above
      logger.error('=== UNEXPECTED ERROR IN handleSendTemplate ===');
      logger.error('Error:', e);
      
      // Extract error message (use error state if already set from inner catch)
      const errorMessage = e?.message || e?.response?.data?.message || "An unexpected error occurred. Please try again.";
      
      // Update error state if not already set by inner catch
      setError(prevError => prevError || errorMessage);
      setSuccess(null); // Clear success message on error
      
      // Ensure failed status is set for the optimistic message
      setMessages((m) =>
        m.map((mm) => (mm.id === tempId && mm.status === "sending" ? { ...mm, status: "failed" } : mm))
      );
    } finally {
      setSending(false);
    }
  }

  function handleInsertTemplateIntoDraft() {
    if (!selectedTemplate) return;
    let text = selectedTemplate.content;
    // Optionally keep existing draft by appending
    text = draft ? draft + (draft.endsWith("\n") ? "" : "\n") + text : text;
    setDraft(text);
  }

  async function handleStopAuto() {
    try {
      setStopping(true);
      // Use the selected customer's phone number from session storage
      const selectedCustomer = sessionStorage.getItem('selectedCustomer');
      const customerPhone = selectedCustomer ? JSON.parse(selectedCustomer).phone : contactId;

      const phoneNumberId = sessionStorage.getItem('phoneNumberId') || '';
      if (!phoneNumberId) {
        setError('Phone number not selected');
        return;
      }
      const newBotSettings = !autoEnabled;

      await stopAutoMessages(apiBaseUrl, {
        contactId: customerPhone,
        phoneNumberId,
        authToken,
        botSettings: newBotSettings
      });
      setAutoEnabled(newBotSettings);
      onStopAutoMessages?.();
    } catch (e: any) {
      setError(e?.message ?? "Could not update auto messages");
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className={classNames("flex h-full min-h-[560px] flex-col rounded-2xl border bg-gray-100", className)}>
      <Header
        contactName={customerData ? customerData.name : `Contact ${contactId}`}
        contactPhone={customerData ? customerData.phone : undefined}
        onBack={handleBackNavigation}
      />

      {/* Tab Navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === "messages" && (
        <>
          <AutoMessagesButton
        autoEnabled={autoEnabled}
        onStopAuto={handleStopAuto}
        stopping={stopping}
      />
      {/* Two-column chat region (left/right bubbles handled in MessageBubble) */}
      {conversationLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500">Loading conversation history...</div>
        </div>
      ) : conversationHistory ? (
        <ConversationHistoryDisplay conversations={conversationHistory.conversations} />
      ) : (
        <MessageList messages={messages} />
      )}

      {/* Template variables editor (only when template mode and variables exist) */}
      {false && selectedTemplate && (
        (() => {
          const keys = parseTemplateVariables(selectedTemplate.content);
          if (keys.length === 0) return null;
          return (
            <div className="border-t bg-white/70">
              <div className="mx-auto max-w-3xl p-3">
                <div className="mb-2 text-xs font-semibold text-gray-600">Template variables</div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {keys.map((k) => (
                    <label key={k} className="text-xs">
                      <span className="mb-1 block text-gray-600">{k}</span>
                      <input
                        className="w-full rounded-md border px-2 py-1"
                        value={tplVars[k] ?? ""}
                        onChange={(e) => setTplVars((prev) => ({ ...prev, [k]: e.target.value }))}
                        placeholder={`Enter ${k}`}
                      />
                    </label>
                  ))}
                </div>
              </div>

            </div>
          );
        })()
      )}

      {/* Composer */}
      <div className="border-t bg-white">
        <div className="mx-auto max-w-3xl p-3">
          <Composer
            mode={mode}
            setMode={(m) => setMode(m)}
            draft={draft}
            setDraft={setDraft}
            onSendPlain={handleSendPlain}
            onSendTemplate={handleSendTemplate}
            sending={sending}
            canSend={canSend}
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={(id) => {
              setSelectedTemplateId(id);
              if (id) setMode("template");
            }}
            selectedTemplate={selectedTemplate}
            tplVars={tplVars}
            setTplVars={setTplVars}
            variableKeys={variableKeys}
            messages={messages}
            conversationHistory={conversationHistory || null}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
          />
        </div>
      </div>
        </>
      )}

      {activeTab === "whatsapp-calls" && (
        <WhatsAppCallsTab
          customerData={customerData}
          participantIdentity={participantIdentity}
          participantName={participantName}
        />
      )}
      {activeTab === "phone-calls" && (
        <PhoneCallsTab
          customerData={customerData}
          participantIdentity={participantIdentity}
          participantName={participantName}
        />
      )}
      {activeTab === "insights" && <InsightsTab />}
      {activeTab === "recommendations" && <RecommendationsTab />}

      {error && (
        <div className="border-t bg-red-50">
          <div className="mx-auto max-w-3xl p-3 text-sm text-red-700">{error}</div>
        </div>
      )}
      
      {success && (
        <div className="border-t bg-green-50">
          <div className="mx-auto max-w-3xl p-3 text-sm text-green-700">{success}</div>
        </div>
      )}
    </div>
  );
}
